import type {
  FacilityProfileJurisdictionContext,
  ProfileSectionResponse,
} from "@services/facilityManagement";

const STATE_LICENSE_RENEWAL_DEFAULTS = {
  cycle_years_key: "1",
  submit_lead_days: "30",
  anchor_kind_key: "expiration_anniversary",
} as const;

function valueMissing(val: unknown): boolean {
  return val === null || val === undefined || (typeof val === "string" && val.trim() === "");
}

export function applyStateLicenseRenewalDefaults(
  sections: ProfileSectionResponse[],
  values: Record<string, unknown>,
  jurisdictionContext?: FacilityProfileJurisdictionContext | null,
): Record<string, unknown> {
  const result = { ...values };
  const hasQualifiedStateLicenseRenewal =
    jurisdictionContext?.reviewed === true &&
    (jurisdictionContext.state_license_renewal_cycle_years != null ||
      jurisdictionContext.state_license_renewal_submit_lead_days != null ||
      Boolean(jurisdictionContext.state_license_renewal_anchor_kind) ||
      Boolean(jurisdictionContext.state_license_renewal_rule_notes));

  for (const section of sections) {
    for (const field of section.fields) {
      if (field.visible === false || !valueMissing(result[field.field_key])) {
        continue;
      }
      if (field.field_key === "state_license_renewal_cycle_years") {
        const jurisdictionCycle =
          jurisdictionContext?.state_license_renewal_cycle_years != null
            ? String(jurisdictionContext.state_license_renewal_cycle_years)
            : null;
        const opt = field.options?.find(
          (o) =>
            o.key ===
            (jurisdictionCycle ?? STATE_LICENSE_RENEWAL_DEFAULTS.cycle_years_key),
        );
        if (opt) {
          result[field.field_key] = opt.key;
        }
      }
      if (field.field_key === "state_license_renewal_submit_lead_days") {
        if (jurisdictionContext?.state_license_renewal_submit_lead_days != null) {
          result[field.field_key] = String(
            jurisdictionContext.state_license_renewal_submit_lead_days,
          );
        } else if (!hasQualifiedStateLicenseRenewal) {
          result[field.field_key] = STATE_LICENSE_RENEWAL_DEFAULTS.submit_lead_days;
        }
      }
      if (field.field_key === "state_license_renewal_anchor_kind") {
        const jurisdictionAnchor =
          jurisdictionContext?.state_license_renewal_anchor_kind ?? null;
        const opt = field.options?.find(
          (o) =>
            o.key ===
            (jurisdictionAnchor ?? STATE_LICENSE_RENEWAL_DEFAULTS.anchor_kind_key),
        );
        if (opt) {
          result[field.field_key] = opt.key;
        }
      }
      if (
        field.field_key === "state_license_renewal_rule_notes" &&
        jurisdictionContext?.state_license_renewal_rule_notes
      ) {
        result[field.field_key] = jurisdictionContext.state_license_renewal_rule_notes;
      }
    }
  }
  return result;
}
